import React, { useEffect, useRef, useState } from 'react';
import analyticsApi, { UserSummaryResponse } from '../../../../api/analyticsApi';
import { useAuth } from '../../../../AuthContext';
import { useEmulation } from '../../../../contexts/EmulationContext';
import Sparkline from '../../../analytics/Sparkline';
import { describeEvent, formatCompactNumber } from '../../../../utils/analyticsFormatters';
import { getRelativeTimeString, formatDate } from '../../../../utils/dateUtils';

interface StatsBlockProps {
  /** Fetch only while the sheet shows the profile view. */
  active: boolean;
}

const EVENT_EMOJI: Record<string, string> = {
  'tend.created': '🌱',
  'post.published': '📣',
  'post.failed': '⚠️',
  'video.generated': '🎬',
  'content.imported': '📥',
};

function browserLabel(userAgent?: string | null): string {
  if (!userAgent) return 'Web';
  return /mobile|iphone|android/i.test(userAgent) ? 'Mobile' : 'Web';
}

/**
 * Personal activity stats — same data flow as the old ProfileStatsSection
 * (emulation-aware via effectiveUser), restyled for glass.
 */
const StatsBlock: React.FC<StatsBlockProps> = ({ active }) => {
  const { isAuthenticated } = useAuth();
  const { effectiveUser, isEmulating } = useEmulation();

  const [summary, setSummary] = useState<UserSummaryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const lastFetchedUserIdRef = useRef<string | null>(null);

  const effectiveUserId = effectiveUser ? String((effectiveUser as any).id) : null;

  useEffect(() => {
    if (!active || !isAuthenticated || !effectiveUserId) return;
    if (lastFetchedUserIdRef.current === effectiveUserId && summary && !error) return;

    let cancelled = false;
    setLoading(true);
    setError(false);
    analyticsApi
      .getUserSummary(effectiveUserId)
      .then((response) => {
        if (cancelled) return;
        setSummary(response);
        lastFetchedUserIdRef.current = effectiveUserId;
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, isAuthenticated, effectiveUserId, error]);

  if (!isAuthenticated || !effectiveUserId) return null;

  const retry = () => {
    lastFetchedUserIdRef.current = null;
    setError(false);
    setSummary(null);
  };

  return (
    <div>
      {/* Section title lives above (in ProfileView); only the emulation
          context needs calling out here. */}
      {isEmulating && (
        <div
          className="tend-micro"
          style={{ color: 'var(--accent-blue-soft)', marginBottom: 10 }}
        >
          {summary ? `${summary.user.name}'s activity` : 'Emulated user'} · emulating
        </div>
      )}

      {error ? (
        <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
          Couldn&apos;t load activity.{' '}
          <button type="button" className="tend-link tend-link--prose" onClick={retry}>
            Retry
          </button>
        </div>
      ) : !summary || (loading && lastFetchedUserIdRef.current !== effectiveUserId) ? (
        <div className="tend-progress" style={{ margin: '10px 0' }}>
          <i style={{ width: '40%' }} />
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            {[
              { label: 'Tends', value: summary.stats.tends_created },
              { label: 'Posts', value: summary.stats.posts_published },
              { label: 'Logins', value: summary.stats.logins },
            ].map((tile) => (
              <div
                key={tile.label}
                style={{
                  background: 'rgba(255,255,255,.08)',
                  border: '1px solid rgba(255,255,255,.16)',
                  borderRadius: 12,
                  padding: '8px 10px',
                }}
              >
                <div
                  style={{
                    fontFamily: "'Inter Tight', sans-serif",
                    fontWeight: 700,
                    fontSize: 17,
                    color: 'var(--text)',
                  }}
                >
                  {formatCompactNumber(tile.value)}
                </div>
                <div className="tend-micro">{tile.label}</div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 12 }}>
            <div className="tend-micro" style={{ marginBottom: 4 }}>
              Activity · last 14 days
            </div>
            <Sparkline
              values={summary.activity.map((day) => ({ label: day.date, count: day.count }))}
            />
          </div>

          {summary.recent_items.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div className="tend-micro" style={{ marginBottom: 4 }}>Recent work</div>
              <div>
                {summary.recent_items.slice(0, 4).map((item) => (
                  <div
                    key={item.id}
                    style={{
                      padding: '7px 0',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 10,
                      borderTop: '1px solid rgba(255,255,255,.10)',
                    }}
                  >
                    <span
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 10,
                        background: 'rgba(255,255,255,.10)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 13,
                        flex: 'none',
                      }}
                    >
                      {EVENT_EMOJI[item.event_type] || '•'}
                    </span>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div
                        style={{
                          fontSize: 13,
                          color: 'var(--text)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {(item.metadata?.title as string) || item.event_type}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: 'var(--text-3)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {describeEvent(item)}
                      </div>
                    </div>
                    <span className="tend-micro" style={{ whiteSpace: 'nowrap', paddingTop: 3 }}>
                      {getRelativeTimeString(item.created_at)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {summary.sessions.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div className="tend-micro" style={{ marginBottom: 4 }}>Sessions</div>
              {summary.sessions.slice(0, 4).map((session, index) => (
                <div
                  key={`${session.created_at}-${index}`}
                  style={{
                    padding: '5px 0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 12,
                    borderTop: index > 0 ? '1px dashed rgba(255,255,255,.10)' : undefined,
                  }}
                >
                  <span style={{ color: 'var(--text-2)', fontWeight: 500 }}>
                    {formatDate(session.created_at)}
                  </span>
                  <span className="tend-micro">
                    {browserLabel(session.user_agent)}
                    {session.client_ip ? ` · ${session.client_ip}` : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default StatsBlock;
